import React, { useMemo } from 'react';
import { Table, type TableColumn } from '@/components/ui/Table';
import { Button } from '@/components/ui/Button/Button';
import { Badge } from '@/components/ui/Badge/Badge';
import { SimpleTooltip } from '@/components/ui/Tooltip';
import { AIIcon } from '@/components/ui/AIIcon/AIIcon';
import { HStack, VStack } from '@/components/ui/Stack';
import { TableActions, TableActionButton } from '@/components/ui';
import { Plus, Plane, Loader2, AlertTriangle, RotateCcw } from 'lucide-react';
import { TIMING } from '@/constants/timing';
import { VenueCard } from './VenueCard';
import { NoteIcon } from '../../NoteIcon';
import type { VenueDetails, ShortlistWithVenue } from '@/services/ResearchService';
import { StarRating } from '@/components/ui/StarRating';

interface SearchResultsTableProps {
  venues: VenueDetails[];
  shortlistedVenueIds: Set<string>;
  shortlistItems?: Map<string, ShortlistWithVenue>; // Map of venue_id -> shortlist item
  onAddToShortlist: (venue: VenueDetails) => void;
  onViewDetails: (venue: VenueDetails) => void;
  onUpdateNotes?: (shortlistId: string, notes: string, clientFacing: boolean) => void | Promise<void>;
  addingVenueId?: string | null;
  showAIScores?: boolean;
  showCityBadge?: boolean;
  // AI Research controls
  onRunResearch?: (venue: VenueDetails) => void;
  onResetResearch?: (venue: VenueDetails) => void;
  enrichingVenueIds?: Set<string>;
  canRunResearch?: boolean;
  // Bulk enrichment
  onBulkEnrich?: () => void;
  venuesNeedingEnrichment?: number;
  isBulkEnriching?: boolean;
  // Total count in destination
  totalInDestination?: number;
  destinationName?: string;
}

export function SearchResultsTable({
  venues,
  shortlistedVenueIds,
  shortlistItems = new Map(),
  onAddToShortlist,
  onViewDetails,
  onUpdateNotes,
  addingVenueId = null,
  showAIScores = true,
  onRunResearch,
  onResetResearch,
  enrichingVenueIds = new Set(),
  canRunResearch = false,
  onBulkEnrich,
  venuesNeedingEnrichment = 0,
  isBulkEnriching = false,
  totalInDestination,
  destinationName,
  showCityBadge = false,
}: SearchResultsTableProps) {
  const columns = useMemo<TableColumn<VenueDetails>[]>(() => [
    {
      key: 'property_name',
      title: 'Property Name',
      sortable: true,
      render: (value: string, record: VenueDetails) => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => onViewDetails(record)}
            className="font-medium text-secondary-600 hover:text-secondary-700 hover:underline text-left"
          >
            {value}
          </button>
          {showCityBadge && (record as any)._sourceCity && (
            <Badge variant="secondary" className="text-xs px-1.5 py-0 flex-shrink-0">
              {(record as any)._sourceCity}
            </Badge>
          )}
        </div>
      )
    },
    {
      key: 'number_of_rooms',
      title: 'Rooms',
      sortable: true,
      render: (value?: number) => value?.toLocaleString() || '-'
    },
    {
      key: 'maximum_capacity',
      title: 'Max Capacity',
      sortable: true,
      render: (value?: number) => value?.toLocaleString() || '-'
    },
    {
      key: 'nearby_airports',
      title: 'Nearby Airports',
      render: (_value: any, record: VenueDetails) => {
        // Check if nearby_airports exists (it might not be in VenueDetails type)
        const airports = (record as any).nearby_airports || [];
        
        // Fallback to nearest_airport if nearby_airports is not available
        if (airports.length === 0 && record.nearest_airport_iata) {
          const distance = record.nearest_airport_distance_km 
            ? Number(record.nearest_airport_distance_km)
            : null;
          
          return (
            <SimpleTooltip
              content={distance
                ? `${record.nearest_airport_name || record.nearest_airport_iata} - ${distance.toFixed(1)}km away`
                : record.nearest_airport_name || record.nearest_airport_iata}
              side="top"
            >
              <div className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs">
                <Plane className="w-3 h-3" />
                <span className="font-medium">{record.nearest_airport_iata}</span>
                {distance && (
                  <span className="text-blue-500">{distance.toFixed(0)}km</span>
                )}
              </div>
            </SimpleTooltip>
          );
        }
        
        if (airports.length === 0) {
          return (
            <div 
              className="inline-flex items-center gap-1 text-gray-400"
              title="No airports assigned"
            >
              <Plane className="w-3 h-3" />
              <span className="text-xs">None</span>
            </div>
          );
        }
        
        return (
          <div className="flex flex-wrap gap-1">
            {airports.slice(0, 2).map((airport: any) => (
              <SimpleTooltip
                key={airport.iata}
                content={`${airport.name} - ${airport.distance_km.toFixed(1)}km away`}
                side="top"
              >
                <div className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs">
                  <Plane className="w-3 h-3" />
                  <span className="font-medium">{airport.iata}</span>
                  <span className="text-blue-500">{airport.distance_km.toFixed(0)}km</span>
                </div>
              </SimpleTooltip>
            ))}
            {airports.length > 2 && (
              <div className="relative">
                <SimpleTooltip
                  content={
                    <div className="space-y-1">
                      <div className="font-medium text-xs">Additional Airports:</div>
                      {airports.slice(2).map((airport: any) => (
                        <div key={airport.iata} className="flex items-center justify-between gap-2 text-xs">
                          <div className="flex items-center gap-1">
                            <Plane className="w-3 h-3" />
                            <span className="font-medium">{airport.iata}</span>
                            <span className="text-gray-500">{airport.name}</span>
                          </div>
                          <span className="text-gray-600">{airport.distance_km.toFixed(1)}km</span>
                        </div>
                      ))}
                    </div>
                  }
                  side="top"
                >
                  <div className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs cursor-help">
                    <span>+{airports.length - 2} more</span>
                  </div>
                </SimpleTooltip>
              </div>
            )}
          </div>
        );
      }
    },
    {
      key: 'star_rating',
      title: 'Stars',
      sortable: true,
      render: (value?: number) => value ? <StarRating rating={value} size="sm" showEmptyStars={true} /> : '-'
    },
    {
      key: 'tripadvisor_rating',
      title: 'TripAdvisor',
      sortable: true,
      render: (_value: any, record: VenueDetails) => {
        const rating = record.tripadvisor_rating ? Number(record.tripadvisor_rating) : null;
        
        if (!rating) return '-';
        
        return (
          <div className="inline-flex items-center gap-1">
            <span className="text-sm text-gray-600">{rating.toFixed(1)}</span>
            {record.tripadvisor_review_count && (
              <span className="text-xs text-gray-500">({record.tripadvisor_review_count})</span>
            )}
          </div>
        );
      }
    },
    {
      key: 'enrichment_status',
      title: 'AI Research',
      render: (value: string, record: VenueDetails) => {
        const enrichmentDate = record.enrichment_completed_at 
          ? new Date(record.enrichment_completed_at).toLocaleString()
          : null;
        
        const jobStatus = record.enrichment_job_status;
        const isQueued = jobStatus === 'queued' || enrichingVenueIds.has(record.id);
        const isProcessing = jobStatus === 'processing';
        const hasActiveJob = isQueued || isProcessing;
        
        const processingDuration = record.enrichment_job_started_at 
          ? Date.now() - new Date(record.enrichment_job_started_at).getTime()
          : 0;
        
        const isStuck = isProcessing && processingDuration > TIMING.TIMEOUTS.JOB_STUCK;
        
        return (
          <div className="flex items-center gap-2">
            {hasActiveJob ? (
              <>
                <div className="flex items-center gap-2">
                  {isStuck && <AlertTriangle className="w-4 h-4 text-orange-500" />}
                  <Loader2 className="w-4 h-4 animate-spin text-primary-600" />
                  <Badge variant={isStuck ? 'error' : isProcessing ? 'warning' : 'default'}>
                    {isStuck ? 'Stuck' : isProcessing ? 'Processing' : 'Queued'}
                  </Badge>
                </div>
                {isStuck && canRunResearch && onResetResearch && (
                  <SimpleTooltip content="Reset Research (Cancel & Revert)" side="top">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onResetResearch(record)}
                    >
                      <RotateCcw className="w-4 h-4 text-orange-500" />
                    </Button>
                  </SimpleTooltip>
                )}
              </>
            ) : (
              <>
                {canRunResearch && onRunResearch && (
                  <SimpleTooltip content="Run Venue Research" side="top">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onRunResearch(record)}
                      className="hover:bg-accent-50"
                    >
                      <AIIcon className="w-4 h-4" />
                    </Button>
                  </SimpleTooltip>
                )}
                
                <SimpleTooltip
                  content={
                    enrichmentDate 
                      ? `Last researched: ${enrichmentDate}${record.enrichment_source ? ` (${record.enrichment_source})` : ''}`
                      : 'Not yet researched'
                  }
                  side="top"
                >
                  <Badge 
                    variant={
                      value === 'enriched' ? 'success' : 
                      value === 'failed' ? 'error' : 'default'
                    }
                  >
                    {value === 'enriched' ? 'researched' : value}
                  </Badge>
                </SimpleTooltip>
              </>
            )}
          </div>
        );
      }
    },
    ...(onUpdateNotes ? [{
      key: 'notes',
      title: 'Notes',
      render: (_value: any, record: VenueDetails) => {
        const shortlistItem = shortlistItems.get(record.id);
        if (!shortlistItem) return null;
        
        return (
          <NoteIcon
            notes={shortlistItem.notes}
            clientFacing={shortlistItem.notes_client_facing}
            venueName={record.property_name}
            onUpdateNotes={(notes, clientFacing) => onUpdateNotes(shortlistItem.id, notes, clientFacing)}
          />
        );
      }
    } as TableColumn<VenueDetails>] : []),
    {
      key: 'actions',
      title: 'Actions',
      render: (_: any, record: VenueDetails) => {
        const isShortlisted = shortlistedVenueIds.has(record.id);
        const isAdding = addingVenueId === record.id;
        
        if (isShortlisted) {
          return (
            <Badge variant="success" className="text-xs">
              Shortlisted
            </Badge>
          );
        }
        
        return (
          <TableActions>
            <TableActionButton
              icon={Plus}
              onClick={() => onAddToShortlist(record)}
              disabled={isAdding}
            >
              Add to Shortlist
            </TableActionButton>
          </TableActions>
        );
      }
    }
  ], [
    enrichingVenueIds,
    canRunResearch,
    onRunResearch,
    onResetResearch,
    shortlistedVenueIds,
    shortlistItems,
    addingVenueId,
    onAddToShortlist,
    onViewDetails,
    onUpdateNotes
  ]);

  return (
    <div>
      
      {/* Mobile: Card View */}
      <div className="md:hidden">
        {venues.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <p className="text-sm">No venues found</p>
          </div>
        ) : (
          <VStack gap={3}>
            {venues.map((venue) => {
              const isShortlisted = shortlistedVenueIds.has(venue.id);
              const isAdding = addingVenueId === venue.id;
              const isEnriching = enrichingVenueIds.has(venue.id);
              const shortlistItem = shortlistItems.get(venue.id);
              
              const jobStatus = venue.enrichment_job_status;
              const isQueued = jobStatus === 'queued' || isEnriching;
              const isProcessing = jobStatus === 'processing';
              const hasActiveJob = isQueued || isProcessing;
              
              return (
                <div key={venue.id}>
                  <VenueCard
                    venue={venue}
                    isShortlisted={isShortlisted}
                    onAddToShortlist={!isShortlisted ? () => onAddToShortlist(venue) : undefined}
                    onViewDetails={() => onViewDetails(venue)}
                    isAddingToShortlist={isAdding}
                    showAIScores={showAIScores}
                    onRunResearch={canRunResearch && !hasActiveJob ? () => onRunResearch?.(venue) : undefined}
                    onResetResearch={canRunResearch && hasActiveJob ? () => onResetResearch?.(venue) : undefined}
                    isEnriching={isEnriching}
                    canRunResearch={canRunResearch}
                  />
                  {isShortlisted && shortlistItem && onUpdateNotes && (
                    <div className="mt-2 px-3 flex items-center justify-end">
                      <NoteIcon
                        notes={shortlistItem.notes}
                        clientFacing={shortlistItem.notes_client_facing}
                        venueName={venue.property_name}
                        onUpdateNotes={(notes, clientFacing) => onUpdateNotes(shortlistItem.id, notes, clientFacing)}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </VStack>
        )}
      </div>
      
      {/* Desktop: Table View */}
      <div className="hidden md:block">
        <Table
          data={venues}
          columns={columns}
          loading={false}
          searchable={false}
          pagination={false}
          emptyText="No venues found"
        />
      </div>
    </div>
  );
}

